import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, IndianRupee, Zap, Sun, PiggyBank, BadgePercent } from "lucide-react";
import { useQuote } from "./QuoteModal";

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15";

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export const SolarCalculator = ({ source = "calculator" }) => {
  const { openQuote } = useQuote();
  const [units, setUnits] = useState("");
  const [bill, setBill] = useState("");
  const [propertyType, setPropertyType] = useState("Residential");
  const [area, setArea] = useState("");
  const [systemType, setSystemType] = useState("On-Grid");
  const [result, setResult] = useState(null);

  const calculate = (e) => {
    e.preventDefault();
    let monthlyUnits = parseFloat(units) || 0;
    if (!monthlyUnits && parseFloat(bill) > 0) monthlyUnits = parseFloat(bill) / 6.5;
    if (!monthlyUnits) {
      setResult(null);
      return;
    }
    let kw = monthlyUnits / 126; // ~4.2 units/day per kW in Tamil Nadu
    const areaSqft = parseFloat(area) || 0;
    if (areaSqft > 0) kw = Math.min(kw, areaSqft / 90);
    kw = Math.max(1, Math.round(kw * 2) / 2);

    const annualGen = kw * 1500;
    const monthlySavings = Math.min(monthlyUnits, kw * 126) * 7;
    const ratePerKw = kw <= 3 ? 60000 : kw <= 10 ? 55000 : 50000;
    const grossCost = kw * ratePerKw;
    let subsidy = 0;
    if (propertyType === "Residential" && systemType !== "Off-Grid") {
      subsidy = Math.min(kw, 2) * 30000 + Math.min(Math.max(kw - 2, 0), 1) * 18000;
      subsidy = Math.min(subsidy, 78000);
    }
    const netCost = grossCost - subsidy;
    const annualSavings = monthlySavings * 12;
    const payback = annualSavings > 0 ? (netCost / annualSavings).toFixed(1) : null;

    setResult({ kw, annualGen, monthlySavings, annualSavings, grossCost, subsidy, netCost, payback });
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="solar-calculator">
      <form onSubmit={calculate} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Avg Monthly EB Units</label>
            <input className={inputCls} placeholder="e.g. 350" value={units} onChange={(e) => setUnits(e.target.value)} inputMode="numeric" data-testid="calc-input-units" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Monthly Bill (₹)</label>
            <input className={inputCls} placeholder="e.g. 2500" value={bill} onChange={(e) => setBill(e.target.value)} inputMode="numeric" data-testid="calc-input-bill" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Property Type</label>
            <select className={inputCls} value={propertyType} onChange={(e) => setPropertyType(e.target.value)} data-testid="calc-select-property">
              {["Residential", "Commercial", "Industrial", "Agriculture"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Rooftop Area (sq.ft)</label>
            <input className={inputCls} placeholder="e.g. 500 (optional)" value={area} onChange={(e) => setArea(e.target.value)} inputMode="numeric" data-testid="calc-input-area" />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Preferred System</label>
          <div className="grid grid-cols-4 gap-2" data-testid="calc-system-type">
            {["On-Grid", "Hybrid", "Off-Grid", "Not Sure"].map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => setSystemType(t)}
                className={`rounded-lg border px-2 py-2.5 text-xs font-bold transition-colors ${
                  systemType === t
                    ? "border-brand-blue bg-brand-blue text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-brand-blue/40"
                }`}
                data-testid={`calc-type-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-blue px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-white transition-all hover:bg-brand-bluedark hover:shadow-lg hover:shadow-brand-blue/25"
          data-testid="calc-submit-btn"
        >
          <Calculator className="h-4 w-4" /> Calculate My Solar Requirement
        </button>
      </form>

      <div className="rounded-2xl border border-slate-100 bg-gradient-to-br from-brand-navy to-brand-bluedark p-6 sm:p-8 text-white sun-rays relative overflow-hidden">
        <AnimatePresence mode="wait">
          {result ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45 }}
              data-testid="calc-results"
            >
              <p className="text-xs font-bold uppercase tracking-widest text-brand-orange">Your Indicative Solar Estimate</p>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-heading text-5xl font-extrabold" data-testid="calc-result-capacity">{result.kw}</span>
                <span className="pb-1.5 text-lg text-white/70">kW system</span>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  { icon: Zap, label: "Annual Generation", value: `${Math.round(result.annualGen).toLocaleString("en-IN")} units` },
                  { icon: PiggyBank, label: "Monthly Savings", value: inr(result.monthlySavings) },
                  { icon: IndianRupee, label: "Indicative System Cost", value: inr(result.grossCost) },
                  { icon: BadgePercent, label: "PM Surya Ghar Subsidy", value: result.subsidy > 0 ? inr(result.subsidy) : "Not applicable" },
                  { icon: IndianRupee, label: "Est. Your Contribution", value: inr(result.netCost) },
                  { icon: Sun, label: "Indicative Payback", value: result.payback ? `${result.payback} yrs` : "—" },
                ].map((r) => (
                  <div key={r.label} className="rounded-xl bg-white/10 p-3.5 backdrop-blur-sm">
                    <r.icon className="h-4 w-4 text-brand-orange" />
                    <p className="mt-2 text-[11px] uppercase tracking-wider text-white/60">{r.label}</p>
                    <p className="font-heading text-lg font-bold">{r.value}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() =>
                  openQuote({
                    property_type: propertyType,
                    solar_requirement: systemType,
                    monthly_units: units || "",
                  })
                }
                className="mt-6 w-full rounded-full bg-brand-orange px-6 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orangedark"
                data-testid="calc-claim-quote-btn"
              >
                Claim This Estimate — Get Exact Quote
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex h-full min-h-[280px] flex-col items-center justify-center text-center"
              data-testid="calc-empty-state"
            >
              <Sun className="h-14 w-14 text-brand-orange animate-spin-slow" strokeWidth={1.2} />
              <p className="mt-4 font-heading text-xl font-semibold">How much can your rooftop save?</p>
              <p className="mt-2 max-w-xs text-sm text-white/60">
                Enter your monthly electricity units or bill amount and see your estimated solar capacity, savings and subsidy.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        <p className="mt-6 text-[11px] leading-relaxed text-white/40">
          All calculations are indicative. Actual system capacity, generation, savings and project cost depend on site
          conditions, electricity consumption, roof orientation, shading, system design, equipment selection and
          applicable regulations.
        </p>
      </div>
    </div>
  );
};
